/**
 * ComparePlansOverlay — in-app floating plan comparison. Mounted once in
 * AppShell; its open state is the `?compare=1` search param, so the browser
 * back button closes it, a refresh keeps it open, and it is deep-linkable.
 *
 * Flow: read ?compare → open dialog → render shared PlanComparison. CTA is
 * billing-aware: a subscribed publisher (subscriptionView kind === "card") opens
 * the Stripe Billing Portal (consistent with /billing's portal-delegated
 * management); an empty-state publisher starts a fresh Stripe Checkout.
 *
 * @layer-2 — domain composition
 */
import { useCallback } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ComparePlansDialog } from "@/shared/components/ComparePlansDialog";
import { PlanComparison } from "./PlanComparison";
import { useSubscription } from "../hooks/useSubscription";
import { useCheckout } from "../hooks/useCheckout";
import { useBillingPortal } from "../hooks/useBillingPortal";
import { subscriptionView } from "../lib/billingHelpers";
import type { BillingInterval, ServiceTier } from "../types";

/** Search-param key + value that opens the overlay. */
export const COMPARE_PARAM = "compare";
export const COMPARE_OPEN = "1";

/**
 * URL-driven plan-comparison overlay. Returns null when `?compare` is absent so
 * it can be mounted unconditionally in the app shell.
 */
export function ComparePlansOverlay() {
  const { t } = useTranslation("pricing");
  const [searchParams, setSearchParams] = useSearchParams();
  const open = searchParams.get(COMPARE_PARAM) === COMPARE_OPEN;

  // Gated on `open`: this component is mounted in AppShell on every authenticated
  // page, so an ungated query would fetch the subscription app-wide. Fetch only
  // while the overlay is open (on /billing it is already cached by the page).
  const { data: subscriptionState, isLoading: subscriptionLoading } = useSubscription({ enabled: open });
  const subscription = subscriptionState?.subscription ?? null;
  const checkout = useCheckout();
  const portal = useBillingPortal();

  /**
   * Radix open-change handler. Opening is owned by callers that set the param;
   * here we only handle close → drop `?compare` (replace, so back still works).
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete(COMPARE_PARAM);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Billing-aware CTA: a subscribed publisher manages via the Stripe Billing
   * Portal; an empty-state publisher starts a fresh Stripe Checkout.
   */
  const handleSelectPlan = useCallback(
    (tier: ServiceTier, interval: BillingInterval) => {
      const view = subscriptionView(subscription ?? null, subscriptionState?.accessState ?? "never_subscribed");
      if (view.kind === "card") {
        portal.mutate();
      } else {
        checkout.mutate({ tier, interval });
      }
    },
    [subscription, portal, checkout],
  );

  if (!open) return null;

  return (
    <ComparePlansDialog open={open} onOpenChange={handleOpenChange} title={t("dashboard.comparePlans")}>
      <PlanComparison
        onSelectPlan={handleSelectPlan}
        pending={checkout.isPending || portal.isPending || subscriptionLoading}
      />
    </ComparePlansDialog>
  );
}
